import React, { Component } from 'react';
import { any, arrayOf, bool, shape } from 'prop-types';
import * as d3 from 'd3';
import { getMessage } from '@tcc/i18n';
import { container } from './css/BaseChart.css';
import styles from './css/DailyPrecipitation.css';
import { QuantityUnitType } from '../../types';
import {
  generateChartConfig,
  getWidthForLength,
  buildLocaleObject,
  formatGraphEntries,
  formatGraphValues,
  getProjections,
} from '../../utils/chartUtils';

const buildLocaleRainfallObject = (rainfall) =>
  buildLocaleObject(rainfall, 'rainfall');

const drawTooltip =
  (element, tooltip, unit, formattedDates, yScale, barAdjustingFactor) =>
  (dataPoint, index) => {
    tooltip.style('display', null).style('opacity', 0.9);

    const precip = formatGraphEntries(dataPoint, unit, 'rainfall');
    const tooltipText = getMessage('chartTooltips.dailyPrecip', {
      value: precip.toString(),
      date: formattedDates[index],
    });

    const adjustingFactor = 5;

    const top = yScale(dataPoint) - parseInt(tooltip.style('height'), 10) - 10;
    const mouseIndex = d3.mouse(element)[0];
    const left = mouseIndex - barAdjustingFactor - adjustingFactor;

    tooltip
      .html(tooltipText)
      .style('left', `${left}px`)
      .style('top', `${top}px`);
  };

const checkForHistoricalData = ({ q }) => q.length;
export class DailyPrecipitation extends Component {
  containerWidth = 0;

  componentDidMount() {
    if (this.refContainer) {
      this.containerWidth = this.refContainer.clientWidth;
    }
    this.createBarChart();
  }

  componentDidUpdate() {
    this.createBarChart();
  }

  createBarChart = () => {
    const { precipitation, endDate, formattedDates, startDate } = this.props;
    const { past, historical } = precipitation;
    const { node } = this;
    d3.select(node)
      .selectAll('*:not(.barContainer):not(.historicalBarContainer)')
      .remove();
    const hasHistoricalData = checkForHistoricalData(historical);
    const { u: unit, q: quantities } = buildLocaleRainfallObject(past);
    const [lowest, possibleHighest] = d3.extent(quantities);
    const highest = Math.max(
      possibleHighest,
      hasHistoricalData
        ? formatGraphValues(d3.max(historical.q), historical.u, 'rainfall')
        : 0
    );
    const total = quantities.length;

    const { padding, yScale, width, timeDiff } = generateChartConfig(
      node,
      highest,
      lowest,
      total,
      startDate,
      endDate,
      this.containerWidth
    );
    const { projectPosition } = getProjections(width, timeDiff);

    const tooltip = d3
      .selectAll(`.${styles.tooltip}`)
      .style('display', 'none')
      .style('opacity', 0);
    const adjustingFactor = padding - 21;
    d3.select(node)
      .selectAll('.barContainer')
      .data(quantities)
      .enter()
      .append('rect')
      .style('fill', '#fe9922')
      .attr('x', (_, i) => projectPosition(i) + adjustingFactor)
      .attr('y', (d) => yScale(d))
      .attr('height', (d) => yScale(0) - yScale(d))
      .attr('width', 1)
      .on(
        'mouseover',
        drawTooltip(
          node,
          tooltip,
          unit,
          formattedDates,
          yScale,
          adjustingFactor
        )
      )
      .on('mouseout', () => {
        tooltip.style('display', 'none').style('opacity', 0);
      });

    if (hasHistoricalData) {
      const normalizedHistoricalPast = buildLocaleRainfallObject(historical);
      const { u: hUnit, q: hQuantities } = normalizedHistoricalPast;
      d3.select(node)
        .selectAll('.historicalBarContainer')
        .data(hQuantities)
        .enter()
        .append('rect')
        .style('fill', '#b0c4de')
        .attr('x', (_, i) => projectPosition(i) + adjustingFactor)
        .attr('y', (d) => yScale(d))
        .attr('height', (d) => yScale(0) - yScale(d))
        .attr('width', 1)
        .on(
          'mouseover',
          drawTooltip(
            node,
            tooltip,
            hUnit,
            formattedDates,
            yScale,
            adjustingFactor
          )
        )
        .on('mouseout', () => {
          tooltip.style('display', 'none').style('opacity', 0);
        });
    }
  };

  render() {
    const {
      precipitation: { past },
    } = this.props;
    const width = getWidthForLength(past.q.length, this.containerWidth) + 50;
    return (
      <div>
        <h2 className={styles.title}>{getMessage('past.precipChartTitle')}</h2>
        <div
          className={container}
          ref={(refContainer) => {
            this.refContainer = refContainer;
          }}
        >
          <svg
            className={styles.svg}
            data-test-id="daily-precipitation-svg"
            height={300}
            ref={(node) => {
              this.node = node;
            }}
            width={width}
          >
            <g className="barContainer" />
            <g className="historicalBarContainer" />
          </svg>
          <div
            className={styles.tooltip}
            data-test-id="daily-precipitation-tooltip"
          />
        </div>
      </div>
    );
  }
}

DailyPrecipitation.propTypes = {
  endDate: any,
  formattedDates: arrayOf(any),
  precipitation: shape({
    past: QuantityUnitType,
    historical: QuantityUnitType,
    help: bool,
  }),
  startDate: any,
};

export default DailyPrecipitation;
